import React from "react";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import axios from "axios";

import DashboardOutlinedIcon        from "@mui/icons-material/DashboardOutlined";
import GroupsOutlinedIcon            from "@mui/icons-material/GroupsOutlined";
import ClassOutlinedIcon             from "@mui/icons-material/ClassOutlined";
import CloudUploadOutlinedIcon       from "@mui/icons-material/CloudUploadOutlined";
import EventNoteOutlinedIcon         from "@mui/icons-material/EventNoteOutlined";
import AddCircleOutlineOutlinedIcon  from "@mui/icons-material/AddCircleOutlineOutlined";
import TuneOutlinedIcon              from "@mui/icons-material/TuneOutlined";
import ArticleOutlinedIcon           from "@mui/icons-material/ArticleOutlined";
import LocationCityOutlinedIcon      from "@mui/icons-material/LocationCityOutlined";
import StyleOutlinedIcon             from "@mui/icons-material/StyleOutlined";
import CalendarMonthOutlinedIcon     from "@mui/icons-material/CalendarMonthOutlined";
import BadgeOutlinedIcon             from "@mui/icons-material/BadgeOutlined";

import DrawerListItem from "../Components/DrawerListItem";
import withAuth       from "../../../Hoc/withAuth";
import { useSidebarAccent } from "../SidebarAccentContext";

const NavSection = ({ label, children, minimized }) => (
  <Box sx={{ mb: 0.5 }}>
    {!minimized && (
      <Typography
        sx={{
          px: 2.25,
          py: 0.75,
          fontSize: "0.6875rem",
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "text.disabled",
        }}
      >
        {label}
      </Typography>
    )}
    {children}
  </Box>
);

const SchoolAdminDrawer = ({ user, token }) => {
  const { minimized } = useSidebarAccent();
  const org     = user?.Org;
  const orgUuid = org?.uuid;

  const myOrg = useSelector((state) => state.myOrg);

  // Live counts for badges
  const { data: uploadsData } = useQuery({
    queryKey: ["drawerUploads", orgUuid],
    queryFn:  () =>
      axios.get(
        `${process.env.REACT_APP_PUBLIC_URL}/manualTracking/getOrgUploads/${orgUuid}`,
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    enabled:  !!orgUuid,
    staleTime: 60_000,
  });

  const pendingUploads  = (uploadsData?.data ?? []).filter((u) => u.status === "Pending").length;
  const pendingStudents = (myOrg?.data?.SchoolStudents ?? []).filter((s) => !s.approved).length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", py: 1 }}>

      {/* ── Navigation ──────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: "auto", pb: 2 }}>

        <NavSection label="Overview" minimized={minimized}>
          <DrawerListItem name="Dashboard"    path="SchoolAdminDashBoard"               Icon={DashboardOutlinedIcon} />
          <DrawerListItem name="Notice Board" path={`mySchool/${orgUuid}`} Icon={ArticleOutlinedIcon} />
        </NavSection>

        <NavSection label="Discover" minimized={minimized}>
          <DrawerListItem name="Events"        path="Events"      Icon={CalendarMonthOutlinedIcon} />
          <DrawerListItem name="Organisations" path="orgs"        Icon={LocationCityOutlinedIcon} />
          <DrawerListItem name="Causes"        path="CausesList"  Icon={StyleOutlinedIcon} />
        </NavSection>

        <NavSection label="Students" minimized={minimized}>
          <DrawerListItem
            name="Students"
            path="SchoolAdmin/StudentsManage"
            Icon={GroupsOutlinedIcon}
            badge={pendingStudents}
          />
          <DrawerListItem
            name="Grades & Classes"
            path={`SchoolAdmin/students/${orgUuid}`}
            Icon={ClassOutlinedIcon}
          />
          <DrawerListItem
            name="Manual Uploads"
            path="manualUploads"
            Icon={CloudUploadOutlinedIcon}
            badge={pendingUploads}
          />
          <DrawerListItem
            name="Teachers"
            path="SchoolAdmin/teachers"
            Icon={BadgeOutlinedIcon}
          />
        </NavSection>

        <NavSection label="Events" minimized={minimized}>
          <DrawerListItem name="My Events"     path={`myEvents/${orgUuid}`}      Icon={EventNoteOutlinedIcon} />
          <DrawerListItem name="Create Event"  path={`CreateEvent/${orgUuid}`}   Icon={AddCircleOutlineOutlinedIcon} />
        </NavSection>

        <NavSection label="Settings" minimized={minimized}>
          <DrawerListItem name="School Profile" path="SchoolAdmin" Icon={TuneOutlinedIcon} exact />
        </NavSection>

      </Box>
    </Box>
  );
};

export default withAuth(SchoolAdminDrawer);
